// What the viewer makes of a file before reading a byte of it. Kept apart from
// loader.ts, which pulls in three.js and the DOM, so the rules stay testable.

/** Mesh formats go through Three.js loaders; the rest through the OCCT worker. */
export const MESH_EXTENSIONS = /\.(glb|gltf|obj|stl|ply|3mf)$/i;
export const ACCEPTED_EXTENSIONS = /\.(stp|step|igs|iges|glb|gltf|obj|stl|ply|3mf)$/i;

/**
 * Above this the browser tends to run out of memory inside the WASM heap.
 * It is a warning, not a wall: some machines chew through far more, and a file
 * that might open is worth the try if the user decides to make it.
 */
export const MAX_FILE_BYTES = 80 * 1024 * 1024;

/** Shown under every rejection, so the dialog always says what does work. */
export const SUPPORTED_FORMATS =
  'Supported: STEP (.stp, .step), IGES (.igs, .iges), glTF (.glb, .gltf), OBJ, STL, PLY and 3MF.';

export function formatMb(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

/**
 * A wrong extension or an empty file cannot work at all. Being large only
 * means it probably will not go well, which is the user's call rather than
 * ours — so that comes back as its own answer, not as a rejection.
 */
export type FileCheck =
  | { kind: 'ok' }
  | { kind: 'blocked'; message: string }
  | { kind: 'oversize'; message: string; detail: string };

/** Only name and size are read, so a plain object does for a caller or a test. */
export function checkFile(file: { name: string; size: number }): FileCheck {
  if (!ACCEPTED_EXTENSIONS.test(file.name)) {
    return {
      kind: 'blocked',
      message: `"${file.name}" has an unsupported extension. Choose a STEP/IGES or mesh file (.glb, .gltf, .obj, .stl, .ply, .3mf).`
    };
  }
  if (file.size === 0) {
    return { kind: 'blocked', message: `"${file.name}" is empty (0 bytes).` };
  }
  if (file.size > MAX_FILE_BYTES) {
    return {
      kind: 'oversize',
      message: `"${file.name}" is ${formatMb(file.size)}, past the ${formatMb(MAX_FILE_BYTES)} where browsers usually run out of memory. It may take several minutes, and the tab can freeze or reload itself.`,
      // Only the OCCT worker can be killed mid-parse; a mesh loader runs in one go.
      detail: MESH_EXTENSIONS.test(file.name)
        ? 'A mesh file cannot be interrupted once it starts. Nothing is uploaded either way.'
        : 'You can stop a STEP/IGES parse with Cancel while it runs. Nothing is uploaded either way.'
    };
  }
  return { kind: 'ok' };
}
